import { useState } from 'react';
import { X, RefreshCw, Pencil, BadgeCheck, Wand2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/api/client';
import { Spinner } from './Spinner';
import { Button } from './Button';
import { toast } from '@/state/toast';
import type {
  PassportStateResponse,
  ProjectPassport,
  PassportField,
  TepItem,
} from '@/types/api';

interface Props {
  open: boolean;
  projectId: string;
  onClose: () => void;
}

/** Phase 13 — persistent project-level passport panel.
 *
 *  GET /projects/{id}/passport/state         — состояние
 *  POST /projects/{id}/passport/extract      — пересчёт
 *  PATCH /projects/{id}/passport             — user-override одного поля
 */
export function PassportPanel({ open, projectId, onClose }: Props) {
  const qc = useQueryClient();
  const enabled = open && !!projectId;

  const { data, isLoading, isError, error } = useQuery<PassportStateResponse>({
    queryKey: ['passport-state', projectId],
    queryFn: () => api.get<PassportStateResponse>(`/projects/${projectId}/passport/state`),
    enabled,
    retry: false,
  });

  const extract = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; passport_json: ProjectPassport }>(
        `/projects/${projectId}/passport/extract?force=true`,
        {},
      ),
    onSuccess: () => {
      toast.success('Паспорт пересчитан');
      qc.invalidateQueries({ queryKey: ['passport-state', projectId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось пересчитать'),
  });

  const patchField = useMutation({
    mutationFn: (body: { field: string; value: unknown }) =>
      api.patch<{ ok: boolean; passport_json: ProjectPassport }>(
        `/projects/${projectId}/passport`,
        body,
      ),
    onSuccess: () => {
      toast.success('Поле обновлено');
      qc.invalidateQueries({ queryKey: ['passport-state', projectId] });
      qc.invalidateQueries({ queryKey: ['project', projectId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось сохранить'),
  });

  if (!open) return null;

  const passport = (data?.passport_json || {}) as ProjectPassport;
  const isEmpty = Object.keys(passport).length === 0;
  const objectTypeValue =
    (passport as Record<string, PassportField | undefined>).object_type?.value;
  // Для linear-объектов функциональное назначение нерелевантно — скрываем.
  const visibleOrder = ORDER.filter(([fid]) => {
    if (fid === 'functional_category' && objectTypeValue === 'linear') return false;
    return true;
  });

  return (
    <>
      <div className="fixed inset-0 z-30" style={{ background: 'rgba(10,22,40,.3)' }} onClick={onClose} />
      <aside
        className="fixed top-0 right-0 h-full bg-white border-l border-border flex flex-col z-40"
        style={{ width: 520, maxWidth: '100%', boxShadow: '-16px 0 32px rgba(10,22,40,.12)' }}
      >
        <header
          className="border-b border-border flex items-center justify-between"
          style={{ padding: '20px 24px' }}
        >
          <div>
            <h4
              className="font-heading text-ink"
              style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}
            >
              Паспорт объекта
            </h4>
            {data?.passport_extracted_at && (
              <p className="text-muted mt-0.5" style={{ fontSize: 11 }}>
                Обновлён{' '}
                {new Date(data.passport_extracted_at).toLocaleString('ru-RU')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            aria-label="Закрыть"
          >
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto" style={{ padding: 24 }}>
          {isLoading && (
            <div className="text-center" style={{ padding: '32px 0' }}>
              <Spinner size={24} className="text-muted" />
            </div>
          )}
          {isError && !isLoading && (
            <div
              className="rounded-card"
              style={{
                padding: 16,
                background: 'rgba(239,68,68,.05)',
                border: '1px solid rgba(239,68,68,.2)',
              }}
            >
              <p className="text-ink" style={{ fontSize: 13 }}>
                {error instanceof ApiError && error.status === 404
                  ? 'Проект не найден.'
                  : 'Не удалось загрузить паспорт'}
              </p>
            </div>
          )}
          {!isLoading && !isError && isEmpty && (
            <div className="text-center" style={{ padding: '32px 8px' }}>
              <Wand2 size={32} className="text-muted" style={{ margin: '0 auto 12px' }} />
              <p className="text-ink" style={{ fontSize: 14, fontWeight: 500 }}>
                Паспорт пока пуст
              </p>
              <p className="text-muted mt-1" style={{ fontSize: 12 }}>
                Загрузите Пояснительную записку и другие разделы — паспорт
                заполнится автоматически.
              </p>
            </div>
          )}
          {!isLoading && !isEmpty && (
            <div className="flex flex-col" style={{ gap: 16 }}>
              {visibleOrder.map(([fid, label, kind]) => (
                <Row
                  key={fid}
                  fieldId={fid}
                  label={label}
                  kind={kind}
                  required={REQUIRED_FIELDS.has(fid)}
                  field={(passport as Record<string, PassportField | undefined>)[fid]}
                  onSave={(v) => patchField.mutate({ field: fid, value: v })}
                  saving={patchField.isPending}
                />
              ))}
            </div>
          )}
        </div>

        <footer
          className="border-t border-border bg-white flex items-center justify-between"
          style={{ padding: '16px 24px' }}
        >
          <span className="text-muted" style={{ fontSize: 11 }}>
            {Object.keys(passport).length} полей
          </span>
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw size={14} />}
            disabled={extract.isPending}
            onClick={() => extract.mutate()}
          >
            {extract.isPending ? 'Пересчёт…' : 'Пересчитать'}
          </Button>
        </footer>
      </aside>
    </>
  );
}

type RowKind = 'text' | 'object_type' | 'subtype' | 'stage' | 'tep' | 'number';

// Обязательные поля паспорта: если пусто — выделяем амбером.
const REQUIRED_FIELDS = new Set<string>([
  'object_type',
  'project_code',
  'customer',
  'region',
]);

// Опциональные поля, которые «появятся после обработки сметы/ПОС».
const APPEARS_LATER_FIELDS: Record<string, string> = {
  construction_duration_months: 'Появится после обработки ПОС',
  estimated_cost_rub: 'Появится после обработки сметы',
  tep: 'Появится после обработки ПЗ и проектных решений',
};

const OBJECT_TYPE_RU: Record<string, string> = {
  linear: 'Линейный',
  areal: 'Площадной',
};
const SUBTYPE_RU: Record<string, string> = {
  railway: 'Железная дорога',
  pipeline: 'Трубопровод',
  highway: 'Автомобильная дорога',
  power_line: 'ЛЭП',
  telecom: 'Линия связи',
};
const STAGE_RU: Record<string, string> = {
  P: 'Эскизный проект (П)',
  R: 'Рабочий (Р)',
  PD: 'Проектная документация (ПД)',
  RD: 'Рабочая документация (РД)',
};

const ORDER: Array<[string, string, RowKind]> = [
  ['object_type', 'Тип объекта', 'object_type'],
  ['subtype', 'Подтип', 'subtype'],
  ['category_or_class', 'Категория / класс', 'text'],
  ['project_code', 'Шифр проекта', 'text'],
  ['customer', 'Заказчик', 'text'],
  ['general_designer', 'Генпроектировщик', 'text'],
  ['project_stage', 'Стадия проектирования', 'stage'],
  ['region', 'Регион', 'text'],
  ['tep', 'ТЭПы', 'tep'],
  ['construction_duration_months', 'Срок строительства (мес.)', 'number'],
  ['estimated_cost_rub', 'Сметная стоимость (₽)', 'number'],
  ['functional_category', 'Функциональное назначение', 'text'],
];

function Row({
  fieldId,
  label,
  kind,
  required,
  field,
  onSave,
  saving,
}: {
  fieldId: string;
  label: string;
  kind: RowKind;
  required?: boolean;
  field: PassportField | undefined;
  onSave: (value: unknown) => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const isUser = field?.confidence === 'user';
  const hasValue = field !== undefined && field.value !== null && field.value !== '';

  function startEdit() {
    setDraft(formatRaw(field?.value));
    setEditing(true);
  }
  function commit() {
    const parsed = parseDraft(draft, kind);
    onSave(parsed);
    setEditing(false);
  }
  function resetAuto() {
    onSave(null);
    setEditing(false);
  }

  return (
    <div>
      <div className="flex items-center justify-between" style={{ gap: 8 }}>
        <div
          className="text-muted uppercase tracking-wider"
          style={{ fontSize: 11, fontWeight: 600 }}
        >
          {label}
        </div>
        <div className="flex items-center" style={{ gap: 6 }}>
          {hasValue && (
            <span
              className="inline-flex items-center"
              style={{
                gap: 4,
                fontSize: 10,
                padding: '2px 6px',
                borderRadius: 4,
                background: isUser ? 'rgba(245,158,11,.12)' : 'rgba(100,116,139,.1)',
                color: isUser ? '#92400e' : '#475569',
              }}
              title={
                isUser
                  ? 'Подтверждено пользователем — не будет перезаписано авто-извлечением'
                  : 'Извлечено автоматически — можно отредактировать'
              }
            >
              {isUser ? <BadgeCheck size={10} /> : null}
              {isUser ? 'подтверждено' : 'авто'}
            </span>
          )}
          {!editing && (
            <button
              type="button"
              onClick={startEdit}
              className="text-muted"
              style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer' }}
              aria-label="Редактировать"
            >
              <Pencil size={12} />
            </button>
          )}
        </div>
      </div>

      {!editing && (
        <div
          className="text-ink mt-1"
          style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}
        >
          {hasValue ? renderValue(field!.value, kind) : (
            required ? (
              <span
                className="italic"
                style={{ color: '#b45309', fontSize: 13 }}
                title="Обязательное поле — заполните вручную, если не определилось автоматически"
              >
                Не определено автоматически — заполните вручную
              </span>
            ) : (
              <span className="text-muted italic" style={{ fontSize: 13 }}>
                {APPEARS_LATER_FIELDS[fieldId] || 'не извлечено'}
              </span>
            )
          )}
        </div>
      )}

      {editing && (
        <div className="mt-1.5 flex flex-col" style={{ gap: 6 }}>
          {kind === 'object_type' ? (
            <select
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="border border-border rounded-md"
              style={{ padding: '6px 8px', fontSize: 13 }}
            >
              <option value="">— не указан —</option>
              <option value="areal">Площадной</option>
              <option value="linear">Линейный</option>
            </select>
          ) : kind === 'subtype' ? (
            <select
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="border border-border rounded-md"
              style={{ padding: '6px 8px', fontSize: 13 }}
            >
              <option value="">— не указан —</option>
              <option value="railway">Железная дорога</option>
              <option value="pipeline">Трубопровод</option>
              <option value="highway">Автомобильная дорога</option>
              <option value="power_line">ЛЭП</option>
              <option value="telecom">Линия связи</option>
            </select>
          ) : kind === 'stage' ? (
            <select
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="border border-border rounded-md"
              style={{ padding: '6px 8px', fontSize: 13 }}
            >
              <option value="">— не указана —</option>
              <option value="P">Эскизный проект (П)</option>
              <option value="PD">Проектная документация (ПД)</option>
              <option value="RD">Рабочая документация (РД)</option>
              <option value="R">Рабочий (Р)</option>
            </select>
          ) : kind === 'tep' ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              className="border border-border rounded-md font-mono"
              style={{ padding: '6px 8px', fontSize: 12 }}
              placeholder="Каждая строка: Наименование | значение | единица"
            />
          ) : (
            <input
              type={kind === 'number' ? 'number' : 'text'}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="border border-border rounded-md"
              style={{ padding: '6px 8px', fontSize: 13 }}
            />
          )}
          <div className="flex" style={{ gap: 6 }}>
            <Button size="sm" onClick={commit} disabled={saving}>
              Сохранить
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
              Отмена
            </Button>
            {isUser && (
              <Button
                size="sm"
                variant="secondary"
                onClick={resetAuto}
                title="Сбросить user-override — поле снова будет авто-заполняться"
              >
                Сбросить
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function renderValue(v: unknown, kind: RowKind): React.ReactNode {
  if (kind === 'tep' && Array.isArray(v)) {
    return (
      <ul className="text-ink" style={{ fontSize: 13, paddingLeft: 16, margin: 0 }}>
        {(v as TepItem[]).map((t, i) => (
          <li key={i}>
            <span className="text-muted">{t.name}:</span> {String(t.value)}
            {t.unit ? ' ' + t.unit : ''}
          </li>
        ))}
      </ul>
    );
  }
  if (kind === 'number' && typeof v === 'number') {
    return v.toLocaleString('ru-RU');
  }
  if (kind === 'object_type' && typeof v === 'string') {
    return OBJECT_TYPE_RU[v] || v;
  }
  if (kind === 'subtype' && typeof v === 'string') {
    return SUBTYPE_RU[v] || v;
  }
  if (kind === 'stage' && typeof v === 'string') {
    return STAGE_RU[v] || v;
  }
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  return JSON.stringify(v);
}

function formatRaw(v: unknown): string {
  if (v == null) return '';
  if (Array.isArray(v)) {
    return (v as TepItem[])
      .map((t) => `${t.name} | ${t.value}${t.unit ? ' | ' + t.unit : ''}`)
      .join('\n');
  }
  return typeof v === 'string' ? v : String(v);
}

function parseDraft(raw: string, kind: RowKind): unknown {
  const s = raw.trim();
  if (!s) return null;
  if (kind === 'number') {
    const n = Number(s.replace(/\s+/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  if (kind === 'tep') {
    const items: TepItem[] = [];
    for (const line of s.split('\n')) {
      const parts = line.split('|').map((p) => p.trim());
      if (parts.length >= 2 && parts[0]) {
        items.push({
          name: parts[0],
          value: parts[1] ?? '',
          unit: parts[2] || null,
        });
      }
    }
    return items.length ? items : null;
  }
  return s;
}
